// @ts-check

import { api } from '@/api'
import { toCamelCase } from '@/helpers/formatters'
import { useMemoize } from '@vueuse/core'
import * as _ from 'lodash-es'

export const translateCountries = (countries, language) => {
  return _.sortBy(
    countries.map(
      ({
        id,
        code,
        name: countryName,
        translations = null,
        is_eu: isEu = null,
        vat_code: vatCode = null,
        allow_invoicing: allowInvoicing = null
      }) => {
        // If translations are available get label value from there.
        const name = _.has(translations, language)
          ? translations[language]
          : countryName

        return {
          id,
          code,
          name,
          label: name, // So that we don't have to use label-path in the select component.
          is_eu: isEu,
          vat_code: vatCode,
          allow_invoicing: allowInvoicing
        }
      }
    ),
    ['name']
  )
}

/**
 * @type {import('@vueuse/core').UseMemoizeReturn<Promise<CountryResource[]>, [CountryParams]>}}
 */
export const fetchCountries = useMemoize(async (params = {}) => {
  const { data: countries } = await api.get('countries', { params })
  return countries
})

/**
 * @param {string} language
 * @param {CountryParams} params
 */
async function getAndTranslateCountriesFromApi(
  language,
  params = { eu: 0, eta: 0 },
  allowInvoicing = false
) {
  try {
    const response = await fetchCountries(params)
    const countries = translateCountries(response, language)
    if (allowInvoicing) {
      // Remove countries where allow_invoicing is false from invoicing countries
      return countries.filter((invoice) => invoice.allow_invoicing !== 0)
    }
    return countries
  } catch (error) {
    console.error(error)
    return []
  }
}

export const getLanguages = useMemoize(async (language = undefined) => {
  const res = await api.get('system/languages', {
    params: {
      language
    }
  })
  return res.data
})

export const getCountries = async (language = 'fi', allowInvoicing = false) =>
  getAndTranslateCountriesFromApi(language, { eu: 0 }, allowInvoicing)

export const getEuCountries = async (language = 'fi') =>
  getAndTranslateCountriesFromApi(language, { eu: 1 })

export const getEtaCountries = async (language = 'fi') =>
  getAndTranslateCountriesFromApi(language, { eta: 1 })

export const getZipCodesForCity = async (cityId) => {
  const { data: zipCodes } = await api.get(`zip-code-for-city/${cityId}`)
  return toCamelCase(zipCodes)
}

/**
 * @param {CountryResource['id']} countryId
 * @param {*} vatNumber
 * @returns
 */
export const validateCountryVatNumber = async (countryId, vatNumber) => {
  const res = await api.post(`countries/${countryId}/vat-number/validate`, {
    vat_number: vatNumber
  })
  return res.valid
}

export const getVatClasses = useMemoize(async (date = null) => {
  /** @type {import('axios').AxiosResponse<VatClasses>} */
  const res = await api.get('vats/classes', {
    params: {
      date
    }
  })
  return res.data
})
